'use client';

import { memo, useState } from 'react';

import { useBootstrapAuth } from '@/features/auth/hooks/useBootstrapAuth';
import { useAcceptInviteMutation } from '@/features/workspaces/api/workspacesApi';
import { setCurrentWorkspace } from '@/features/workspaces/slices/workspacesSlice';
import { useAppDispatch } from '@/store/hooks';
import {
  Avatar,
  MainButton,
  Tooltip,
  SimpleTable,
  type SimpleTableColumn,
} from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import type { WorkspaceInviteItem } from '@/features/workspaces/types';
import type { Workspace } from '@web/types';

type AcceptedWorkspace = Pick<Workspace, 'name' | 'slug'>;

type Props = {
  items: WorkspaceInviteItem[];
  isLoading?: boolean;
  onAccepted?: (workspace: AcceptedWorkspace) => void;
};

export const InvitationsTable = memo(function InvitationsTable({
  items,
  isLoading = false,
  onAccepted,
}: Props) {
  const dispatch = useAppDispatch();
  const bootstrapAuth = useBootstrapAuth();
  const [acceptInvite, { isLoading: isAccepting }] = useAcceptInviteMutation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);

  const handleAcceptInvite = async (inv: WorkspaceInviteItem) => {
    if (locked) return;
    if (!inv.invite_code) {
      notify({
        type: 'error',
        message: 'This invite can only be accepted from the invited account.',
      });
      return;
    }

    setLocked(true);
    setActiveId(inv.id);
    try {
      const response = await acceptInvite({
        invite_code: inv.invite_code,
      }).unwrap();
      const responseData = response.data;
      const acceptedWorkspace = responseData?.workspace ?? null;

      if (acceptedWorkspace) {
        dispatch(setCurrentWorkspace(acceptedWorkspace));
      }

      if (responseData?.user && acceptedWorkspace) {
        bootstrapAuth({
          currentUser: responseData.user,
          currentWorkspaceSlug: acceptedWorkspace.slug,
          isWorkspaceSetupRequired:
            responseData.is_workspace_setup_required ?? false,
          isWorkspaceOnboarded:
            acceptedWorkspace.is_workspace_onboarded ?? null,
        });
      }

      notify({ type: 'success', message: 'Invite accepted.' });

      onAccepted?.(acceptedWorkspace ?? inv.workspace);
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
      setLocked(false);
    } finally {
      setActiveId(null);
    }
  };

  const columns: Array<SimpleTableColumn<WorkspaceInviteItem>> = [
    {
      header: 'Workspace',
      render: (inv) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            name={inv.workspace.name}
            src={inv.workspace.logo_url || ''}
          />
          <span style={{ fontWeight: 600 }}>{inv.workspace.name}</span>
        </div>
      ),
    },
    { header: 'Primary Domain', render: (inv) => inv.workspace.primary_domain },
    {
      header: 'Status',
      render: (inv) => (
        <span style={{ textTransform: 'capitalize' }}>{inv.status}</span>
      ),
    },
    {
      header: 'Message',
      render: (inv) => (
        <Tooltip title={inv.message || '-'} placement="bottom">
          <span className="truncate-md">{inv.message || '-'}</span>
        </Tooltip>
      ),
    },
    {
      header: '',
      align: 'right',
      width: 140,
      render: (inv) => (
        <MainButton
          onClick={() => handleAcceptInvite(inv)}
          disabled={inv.status !== 'pending' || locked || !inv.invite_code}
          isLoading={isAccepting && activeId === inv.id}
        >
          Join
        </MainButton>
      ),
    },
  ];

  if (!items?.length && !isLoading) return null;

  return (
    <SimpleTable
      title="Your Invitations"
      columns={columns}
      data={items}
      loading={isLoading}
      getRowKey={(r) => r.id}
    />
  );
});

InvitationsTable.displayName = 'InvitationsTable';
